// If you are using CSS modules instead of global CSS,
// you would import it like this:
// import styles from './ApiDocumentation.module.css';
import { useEffect, useState } from "react";
import "./index.css"; // Assuming index.css contains styles for pre, code, .sidebar-link.active, etc.
import { baseURL } from "@/Environment";
import { Link } from "react-router-dom";
import { useUser } from "@/hooks/useUser";

const ApiDocumentation = () => {
  // Note: In a real React application with routing (like React Router),
  // you might manage active link state or scrolling behavior
  // differently (e.g., using useEffect for scroll-into-view or checking window.location.hash).
  // For this static component, clicking links will rely on default browser anchor behavior.
  const [activeHash, setActiveHash] = useState(window.location.hash);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveHash(window.location.hash);
    };

    // Set initial active hash in case the page loads with a hash
    if (window.location.hash) {
      setActiveHash(window.location.hash);
    } else {
      // Optionally, set a default active hash if none is present
      // For example, the first link:
      // setActiveHash(links[0]?.hash || "");
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const links = [
    { hash: "#base-url", label: "Base URL" },
    { hash: "#authentication", label: "Authentication" },
    { hash: "#org-details", label: "Get Organization Details" },
    { hash: "#org-members", label: "Get Organization Members" }, // ADDED THIS LINE
    { hash: "#programs-list", label: "Get Programs by Org ID" },
    { hash: "#portfolios-list", label: "Get Portfolios by Org ID" },
    { hash: "#projects-list", label: "Get Projects by Org ID" },
    { hash: "#projects-by-program", label: "Get Projects by Program ID" },
    { hash: "#projects-by-portfolio", label: "Get Projects by Portfolio ID" },
    {
      hash: "#project-details",
      label: "Get Project Details by ID (Includes Tasks)",
    }, // Updated label for clarity
    { hash: "#tasks-list", label: "Get Tasks by Project ID (See Note)" },
  ];
  const { user } = useUser();
  return (
    // Use flexbox for side-by-side layout, wrapping the aside and main content
    <div className="flex h-screen">
      {/* Sidebar */}
      {/* h-screen sticky top-0 allows the sidebar to take full height and stay visible */}
      {/* overflow-y-auto added to allow scrolling if links exceed sidebar height */}
      <aside className="sidebar-bg text-white w-64 flex-shrink-0 h-screen sticky top-0 overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold">ProjectChef API</h2>
          <p className="text-sm text-gray-400">Data Access Docs</p>
        </div>
        <nav className="mt-6">
          <ul>
            {links.map((link) => (
              <li key={link.hash}>
                <a
                  href={link.hash}
                  className={`sidebar-link block py-2 px-6 text-sm transition-colors duration-200 ease-in-out ${
                    activeHash === link.hash ? "active" : "hover:bg-gray-700" // Use specific class for active state
                  }`}
                  onClick={() => setActiveHash(link.hash)} // Update state on click for immediate feedback
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      {/* flex-grow makes this div take up the remaining space */}
      {/* overflow-y-scroll allows the main content to scroll independently */}

      <div className="flex-grow p-6 overflow-y-scroll">
        {/* Center and style the main content area like the original document container */}
        {/* max-w-3xl sets a maximum width for readability */}
        <div className="container mx-auto max-w-3xl bg-white shadow-lg rounded-lg border-t-4 main-content-border p-6">
          {/* Main title for the documentation page */}
          <h1 className="text-3xl font-bold mb-8 text-center text-gray-900">
            API Endpoints for Data Access
          </h1>
          <div className="flex justify-end my-2">
            <a
              className="!text-primary-600 underline hover:no-underline"
              href={baseURL + "/api-docs"}
              target="_blank"
            >
              Click here to try the API in Swagger UI
            </a>
          </div>
          {/* Base URL Section */}
          <section
            id="base-url"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Base URL
            </h2>
            <p className="mb-4 text-sm text-gray-700">
              All API endpoints described in this documentation are relative to
              the following base URL:
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="text-white break-all">
                {baseURL + "/api/v1"}
              </code>
            </pre>
            <p className="mt-4 mb-4 text-sm text-gray-700">
              For example, the "Get Organization Details" endpoint{" "}
              <code className="text-xs bg-gray-200 p-0.5 rounded text-gray-800">
                /organization
              </code>{" "}
              {/* UPDATED PATH EXAMPLE */}
              would be fully accessed at:
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="text-white break-all">{`${
                baseURL + "/api/v1"
              }/organization`}</code>{" "}
              {/* UPDATED PATH EXAMPLE */}
            </pre>
            <p className="mt-4 text-sm text-gray-700">
              Remember to append the base URL and the <code>/api/</code> prefix
              to all paths listed below.
            </p>
          </section>

          {/* Authentication Section */}
          <section
            id="authentication"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Authentication
            </h2>
            <p className="mb-4 text-sm text-gray-700">
              Head on over to your account settings in ProjectChef. Look for the{" "}
              <Link
                className="!text-primary-600 underline hover:no-underline"
                to={"/organisation/" + user?.userOrganisation[0].organisationId}
                target="_blank"
              >
                Organisation Settings
              </Link>{" "}
              screen, then the{" "}
              <strong className="font-medium text-gray-800">
                Application Settings
              </strong>{" "}
              section. You can generate or view your API tokens there.
            </p>
            <p className="mb-4 text-sm text-gray-700">
              All data retrieval endpoints documented below require
              authentication using these credentials, passed via{" "}
              <strong className="font-medium text-gray-800">
                custom HTTP headers
              </strong>{" "}
              with every request. The organization the request applies to is
              determined by the <code>client-id</code> provided.
            </p>

            <div className="mb-4">
              <p className="font-medium text-sm mb-2 text-gray-800">
                Required Headers:
              </p>
              {/* Explicitly listing headers here as requested */}
              <ul className="list-disc list-inside pl-4 text-sm text-gray-700">
                <li>
                  <code>client-id</code> (string, required): Your unique Client
                  ID generated in Application Settings. This ID identifies your
                  organization.
                </li>
                <li>
                  <code>client-secret</code> (string, required): Your unique
                  Client Secret (API Token) generated in Application Settings.
                  Treat this like a password.
                </li>
              </ul>
            </div>

            <p className="mb-4 text-sm text-gray-700">
              Include both the{" "}
              <code className="text-xs bg-gray-200 p-0.5 rounded text-gray-800">
                client-id
              </code>{" "}
              and{" "}
              <code className="text-xs bg-gray-200 p-0.5 rounded text-gray-800">
                client-secret
              </code>{" "}
              as separate HTTP headers in all requests to the data access
              endpoints described below.
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Failure to provide valid credentials or providing credentials for
              an organization you don't have access to will result in an
              authentication error response (typically 401 Unauthorized or 403
              Forbidden).
            </p>
          </section>

          {/* Get Organization Endpoint */}
          <section
            id="org-details"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Organization Details
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>{" "}
            {/* Using green for GET */}
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /organization
              </code>{" "}
              {/* UPDATED PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches the details of the organization associated with your{" "}
              <code>client-id</code> provided in the authentication headers.
            </p>
            {/* REMOVED Path Parameters */}
            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              {/* Link back to the Authentication section for header details */}
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>
            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "organisation fetch successfully",
    "data": {
        "organisationId": "org-abc-123", // This ID will match your client-id's organization
        "name": "Example Organization Inc.",
        "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
        "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ"
        // ... other organization fields
    }
}`}</code>
            </pre>
          </section>

          {/* Get Organization Members Endpoint */}
          <section
            id="org-members"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Organization Members
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /members
              </code>{" "}
              {/* UPDATED PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches a list of all members associated with the organization
              identified by your <code>client-id</code> provided in the
              authentication headers, including their roles within the
              organization.
            </p>

            {/* REMOVED Path Parameters */}

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Members fetched successfully",
    "data": [
        {
            "userId": "user-123",
            "organisationId": "org-abc-123", // This ID will match your client-id's organization
            "name": "Alice Smith",
            "role": "admin", // Possible values: "admin", "pm", "member"
            "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ"
            // ... other user/membership fields
        },
        {
            "userId": "user-456",
            "organisationId": "org-abc-123", // This ID will match your client-id's organization
            "name": "Bob Johnson",
            "role": "member",
            "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ"
            // ... other user/membership fields
        },
        // ... more members
    ]
}`}</code>
            </pre>
          </section>

          {/* Get Programs by Organization ID Endpoint */}
          <section
            id="programs-list"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Programs by Organization ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /programs
              </code>{" "}
              {/* UPDATED PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches a list of all programs associated with the organization
              identified by your <code>client-id</code> provided in the
              authentication headers.
            </p>

            {/* REMOVED Path Parameters */}

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Programs fetched successfully",
    "data": [
        {
            "programId": "pgm-abc-789",
            "organisationId": "org-abc-123", // This ID will match your client-id's organization
            "name": "Customer Success Program",
            "description": "Oversees initiatives related to customer satisfaction.",
            "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ"
            // ... other program fields
        },
        // ... more programs
    ]
}`}</code>
            </pre>
          </section>

          {/* Get Portfolios by Organization ID Endpoint */}
          <section
            id="portfolios-list"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Portfolios by Organization ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /portfolios
              </code>{" "}
              {/* UPDATED PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches a list of all portfolios associated with the organization
              identified by your <code>client-id</code> provided in the
              authentication headers.
            </p>

            {/* REMOVED Path Parameters */}

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Portfolios fetched successfully",
    "data": [
        {
            "portfolioId": "pf-def-012",
            "organisationId": "org-abc-123", // This ID will match your client-id's organization
            "name": "Strategic Growth Portfolio",
            "description": "Contains projects focused on market expansion.",
            "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ"
            // ... other portfolio fields
        },
        // ... more portfolios
    ]
}`}</code>
            </pre>
          </section>

          {/* Get Projects by Organization ID Endpoint */}
          <section
            id="projects-list"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Projects by Organization ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /projects
              </code>{" "}
              {/* UPDATED PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches a list of all projects associated with the organization
              identified by your <code>client-id</code> provided in the
              authentication headers.
            </p>

            {/* REMOVED Path Parameters */}

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Projects fetched successfully",
    "data": [
        {
            "projectId": "proj-xyz-456",
            "organisationId": "org-abc-123", // This ID will match your client-id's organization
            "programId": null, // May be null or a program ID
            "portfolioId": null, // May be null or a portfolio ID
            "name": "Project Alpha",
            "status": "Active",
             "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
             "deletedAt": null
            // ... other project fields
        },
        // ... more projects
    ]
}`}</code>
            </pre>
          </section>

          {/* Get Projects by Program ID Endpoint */}
          <section
            id="projects-by-program"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Projects by Program ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /projects/program/:programId
              </code>{" "}
              {/* PATH UNCHANGED */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches a list of projects associated with a specific program ID.
              These projects must also belong to the organization identified by
              your <code>client-id</code> provided in the authentication
              headers.
            </p>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Path Parameters:
              </p>
              <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                <li>
                  <code>programId</code> (string, required) - The unique ID of
                  the program whose projects are to be fetched.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Projects fetched successfully by Program ID",
    "data": [
        {
            "projectId": "proj-p-001",
            "organisationId": "org-abc-123", // Should match your client-id's organization
            "programId": "pgm-abc-789", // Should match the requested programId
            "portfolioId": "pf-def-012", // May be null or a portfolio ID within this program/org
            "name": "Program Initiative A",
            "status": "Completed",
             "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
             "deletedAt": null
            // ... other project fields
        },
        // ... more projects within the program
    ]
}`}</code>
            </pre>
          </section>

          {/* Get Projects by Portfolio ID Endpoint */}
          <section
            id="projects-by-portfolio"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Projects by Portfolio ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /projects/portfolio/:portfolioId
              </code>{" "}
              {/* PATH UNCHANGED */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches a list of projects associated with a specific portfolio
              ID. These projects must also belong to the organization identified
              by your <code>client-id</code> provided in the authentication
              headers.
            </p>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Path Parameters:
              </p>
              <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                <li>
                  <code>portfolioId</code> (string, required) - The unique ID of
                  the portfolio whose projects are to be fetched.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Projects fetched successfully by Portfolio ID",
    "data": [
        {
            "projectId": "proj-pf-002",
            "organisationId": "org-abc-123", // Should match your client-id's organization
            "programId": "pgm-abc-789", // May be null or a program ID within this portfolio/org
            "portfolioId": "pf-def-012", // Should match the requested portfolioId
            "name": "Portfolio Project B",
            "status": "In Progress",
             "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
            "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
             "deletedAt": null
            // ... other project fields
        },
        // ... more projects within the portfolio
    ]
}`}</code>
            </pre>
          </section>

          {/* Get Project by ID Endpoint */}
          <section
            id="project-details"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Project Details by Project ID (Includes Tasks)
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /projectbyid/:projectId
              </code>{" "}
              {/* UPDATED PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches the details of a specific project by its ID. This endpoint
              also includes the list of associated tasks nested within the
              project object (as shown in the example). The project must belong
              to the organization identified by your <code>client-id</code>{" "}
              provided in the authentication headers.
            </p>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Path Parameters:
              </p>
              <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                <li>
                  <code>projectId</code> (string, required) - The unique ID of
                  the project to fetch.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Project fetched successfully",
    "data": {
        "projectId": "proj-xyz-456",
        "organisationId": "org-abc-123", // Should match your client-id's organization
        "programId": "pgm-abc-789", // May be null
        "portfolioId": "pf-def-012", // May be null
        "name": "Project Alpha",
        "status": "Active",
         "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
        "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
         "deletedAt": null,
        // ... other project fields
        "tasks": [ // Tasks are nested here
            {
                "taskId": "task-p1-001",
                "projectId": "proj-xyz-456", // Matches parent projectId
                "description": "Implement feature A",
                "status": "Completed",
                "assignedTo": "user-1", // User ID
                 "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
                "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
                "deletedAt": null
                // ... other task fields
            },
            // ... more tasks for this project
        ]
    }
}`}</code>
            </pre>
          </section>

          {/* Get Task by ID Endpoint - ADDED THIS SECTION */}
          <section
            id="task-details"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Task Details by ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /task/:taskId
              </code>{" "}
              {/* NEW PATH */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches the details of a specific task by its unique ID. The task
              must belong to a project within the organization identified by
              your <code>client-id</code> provided in the authentication
              headers.
            </p>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Path Parameters:
              </p>
              <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                <li>
                  <code>taskId</code> (string, required) - The unique ID of the
                  task to fetch.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Task fetched successfully",
    "data": {
        "taskId": "task-abc-789", // The requested taskId
        "projectId": "proj-xyz-456", // The project this task belongs to
        "description": "Complete API docs update",
        "status": "In Progress", // e.g., "Not Started", "In Progress", "Completed"
        "assignedTo": "user-abc-123", // User ID assigned to the task (may be null)
        "dueDate": "YYYY-MM-DD", // Optional due date (may be null)
        "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
        "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
        "deletedAt": null
        // ... other task fields
    }
}`}</code>
            </pre>
          </section>

          {/* Get Tasks by Project ID Endpoint */}
          <section
            id="tasks-list"
            className="mb-8 p-4 border border-gray-200 rounded-lg shadow-sm"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">
              Get Tasks by Project ID
            </h2>
            <p className="mb-2 text-sm font-semibold text-green-600">GET</p>
            <p className="mb-4 text-sm">
              <code className="text-sm bg-gray-100 p-1 rounded text-gray-800">
                /tasks/:projectId
              </code>{" "}
              {/* PATH UNCHANGED */}
            </p>
            <p className="mb-4 text-sm text-gray-700">
              Fetches tasks associated with a specific project ID. The project
              must belong to the organization identified by your{" "}
              <code>client-id</code> provided in the authentication headers.
            </p>
            <p className="mb-4 text-sm text-gray-700">
              <strong>Note:</strong> The{" "}
              <a
                href="#project-details"
                className="text-blue-600 hover:underline"
              >
                Get Project Details by Project ID
              </a>{" "}
              endpoint also includes the tasks nested within the project object.
              This endpoint provides the task list, potentially still wrapped in
              the project object depending on the exact API response structure
              (see example).
            </p>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Path Parameters:
              </p>
              <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                <li>
                  <code>projectId</code> (string, required) - The unique ID of
                  the project whose tasks are to be fetched.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="font-medium text-sm mb-1 text-gray-800">
                Required Headers:
              </p>
              <p className="ml-4 text-sm text-gray-700">
                See{" "}
                <a
                  href="#authentication"
                  className="text-blue-600 hover:underline"
                >
                  Authentication
                </a>{" "}
                section.
              </p>
            </div>

            <p className="font-medium text-sm mb-1 text-gray-800">
              Example Success Response (200 OK):
            </p>
            {/* Keeping the original example structure as requested, even if it wraps tasks in a project object */}
            <pre className="bg-gray-800 text-white p-3 rounded overflow-x-auto">
              <code className="language-json text-white break-all">{`{
    "statusCode": 200,
    "message": "Project fetched successfully", // Message from original example
    "data": {
        "projectId": "proj-xyz-456", // Matches the requested projectId
        "organisationId": "org-abc-123", // Should match your client-id's organization
        "programId": "pgm-abc-789", // May be null
        "portfolioId": "pf-def-012", // May be null
        "name": "Project Alpha",
        "status": "Active",
         "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
        "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
         "deletedAt": null,
        // ... other project fields
        "tasks": [ // The array of tasks you might be primarily interested in
            {
                "taskId": "task-p1-001",
                "projectId": "proj-xyz-456",
                "description": "Implement feature A",
                "status": "Completed",
                "assignedTo": "user-1",
                 "createdAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
                "updatedAt": "YYYY-MM-DDTHH:mm:ss.sssZ",
                "deletedAt": null
                // ... other task fields
            },
            // ... more tasks for this project
        ]
    }
}`}</code>
            </pre>
          </section>

          {/* Footer could go here if needed */}
          {/* <footer className="mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-600">
            <p>© {new Date().getFullYear()} ProjectChef. All rights reserved.</p>
          </footer> */}
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
